import './App.css';

import React, { useEffect, Suspense } from 'react';

import { HashRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider } from "./Context/AuthContext";
import LoginModal from "./components/LoginModal";
import OnboardingModal from "./components/OnboardingModal";
import AliasModal from "./components/AliasModal";
import DeleteModal from "./components/DeleteModal";
import RolePickerModal from "./components/RolePickerModal";

import Header from './components/Header';
import SuspensionBanner from './components/SuspensionBanner';
import Footer from './components/Footer';

// Capacitor: push notification registration (iOS native only)
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { API_BASE } from "./utils/api";

// Eager — always needed on first load (web marketing hero, not shown in app)
import Main from './components/Main';

// Lazy — loaded only when route is visited (reduces initial bundle ~40%)
var Terms            = React.lazy(function() { return import('./pages/Terms'); });
var ComingSoon       = React.lazy(function() { return import('./pages/ComingSoon'); });
var NotFound         = React.lazy(function() { return import('./pages/NotFound'); });
var NewsPage         = React.lazy(function() { return import('./pages/NewsPage'); });
var ArticlePage      = React.lazy(function() { return import('./pages/ArticlesPage'); });
var TeamPage         = React.lazy(function() { return import('./pages/TeamPage'); });
var LeaguePage       = React.lazy(function() { return import('./pages/LeaguePage'); });
var Play             = React.lazy(function() { return import('./components/play/Play'); });
var Standings        = React.lazy(function() { return import('./pages/Standings'); });
var About            = React.lazy(function() { return import('./pages/About'); });
var Discover         = React.lazy(function() { return import('./pages/Discover'); });
var PrivacyPolicy    = React.lazy(function() { return import('./pages/PrivacyPolicy'); });
var LeaguesList      = React.lazy(function() { return import('./pages/LeaguesList'); });
var LeagueDetail     = React.lazy(function() { return import('./pages/LeagueDetail'); });
var PlayerProfile    = React.lazy(function() { return import('./pages/PlayerProfile'); });
var ForgotPassword   = React.lazy(function() { return import('./pages/ForgotPassword'); });
var ResetPassword    = React.lazy(function() { return import('./pages/ResetPassword'); });
var ParentDashboard  = React.lazy(function() { return import('./pages/ParentDashboard'); });
var CoachDashboard   = React.lazy(function() { return import('./pages/CoachDashboard'); });
var RoomsPage        = React.lazy(function() { return import('./pages/RoomsPage'); });
var RoomPage         = React.lazy(function() { return import('./pages/RoomPage'); });
var ModerationDashboard = React.lazy(function() { return import('./pages/ModerationDashboard'); });
var ChildrenPrivacy  = React.lazy(function() { return import('./pages/ChildrenPrivacyNotice'); });

function PageLoader() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-6 h-6 border-2 border-lime-400/30 border-t-lime-400 rounded-full animate-spin" />
        <p className="text-zinc-600 text-xs font-mono uppercase tracking-widest">Loading</p>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("Gametrait ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-3">Something went wrong</p>
            <h1 className="text-2xl font-bold mb-2">Unexpected error</h1>
            <p className="text-zinc-400 text-sm mb-6">
              This page ran into a problem. Try refreshing — if it keeps happening, let us know.
            </p>
            <button
              onClick={function() { window.location.reload(); }}
              className="bg-lime-400 hover:bg-lime-300 text-black font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// On native (Capacitor iOS), the marketing hero at "/" is pointless — the user
// already installed the app. Send them straight to Discover. On web, keep Main
// so the landing page / SEO / download pitch still works.
var isNative = false;
try { isNative = Capacitor.isNativePlatform(); } catch (e) {}

// ── Deep link handler ─────────────────────────────────────────────────────────
// Handles Universal Links on iOS native. When the app is opened via a
// gametrait.com URL (e.g. a password reset email link), Capacitor fires
// appUrlOpen. We parse path+query and navigate to the matching HashRouter route.
// Uses window.Capacitor.Plugins.App to avoid a build-time @capacitor/app import.
function DeepLinkHandler() {
  var navigate = useNavigate();
  useEffect(function() {
    var isNativePlatform = false;
    try { isNativePlatform = Capacitor.isNativePlatform(); } catch (e) {}
    if (!isNativePlatform) return;

    var CapApp = null;
    try { CapApp = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App; } catch (e) {}
    if (!CapApp || !CapApp.addListener) return;

    var handle = CapApp.addListener("appUrlOpen", function(data) {
      try {
        var url = new URL(data.url);
        var route = url.pathname + url.search;
        if (route && route !== "/") navigate(route, { replace: true });
      } catch (e) {}
    });

    return function() {
      try { if (handle && handle.remove) handle.remove(); } catch (e) {}
    };
  }, [navigate]);
  return null;
}

function App() {
  // ── Push registration ──────────────────────────────────────────────────────
  // This effect runs once on mount and walks through every step needed to get
  // an iOS device token into the `push_tokens` D1 table. Every step is logged
  // with a "[push]" prefix so Safari Web Inspector → Console shows exactly
  // where the pipeline stops if it fails. Common failure modes:
  //   [push] not a native platform     → running on web, nothing to do
  //   [push] permission denied         → user said No to the prompt (fix in Settings)
  //   [push] register() threw …        → Push Notifications capability isn't enabled in Xcode
  //   [push] registrationError …       → APNs rejected the app (capability / provisioning issue)
  //   [push] no auth token …           → fired before login. We persist the token and
  //                                       poll until login completes, then POST it.
  //   [push] POST … 401                → JWT was expired or malformed
  //   [push] POST … 500                → Worker error — check Cloudflare logs
  //   [push] POST … 200                → success, row should be in push_tokens

  useEffect(function() {
    // Helpers are defined INSIDE the effect so the [] deps array is honest —
    // ESLint's react-hooks/exhaustive-deps rule otherwise complains they were
    // declared in the component closure but not listed as dependencies.
    function readAuthToken() {
      try {
        return sessionStorage.getItem("gt_token")
            || localStorage.getItem("gt_token")
            || localStorage.getItem("token");
      } catch (e) { return null; }
    }

    async function postPushTokenToServer(deviceToken, source) {
      var authToken = readAuthToken();
      if (!authToken) {
        console.log("[push] no auth token yet — saving for retry (source: " + source + ")");
        try { localStorage.setItem("gt_pending_push_token", deviceToken); } catch (e) {}
        return;
      }
      try {
        console.log("[push] POSTing device token to /api/register-push-token (source: " + source + ")");
        var res = await fetch(API_BASE + "/api/register-push-token", {
          method:  "POST",
          headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
          body:    JSON.stringify({ token: deviceToken, device_type: "ios" }),
        });
        var bodyText = "";
        try { bodyText = await res.text(); } catch (e) {}
        console.log("[push] POST result: status=" + res.status + " body=" + bodyText.slice(0, 200));
        if (res.ok) {
          try { localStorage.removeItem("gt_pending_push_token"); } catch (e) {}
        }
      } catch (err) {
        console.log("[push] POST threw: " + ((err && err.message) || err));
      }
    }

    async function registerPush() {
      try {
        var native = false;
        try { native = Capacitor.isNativePlatform(); } catch (e) {}
        console.log("[push] starting. native=" + native);
        if (!native) {
          console.log("[push] not a native platform — push registration skipped");
          return;
        }

        var permResult;
        try {
          permResult = await PushNotifications.requestPermissions();
          console.log("[push] permission result: " + JSON.stringify(permResult));
        } catch (err) {
          console.log("[push] requestPermissions threw: " + ((err && err.message) || err));
          return;
        }
        if (permResult.receive !== "granted") {
          console.log("[push] permission denied — user must enable in Settings → Gametrait → Notifications");
          return;
        }

        // Listeners must be attached BEFORE register() so we don't miss the
        // event when iOS already has a cached token (subsequent app launches).
        PushNotifications.addListener("registration", function(tokenData) {
          var t = (tokenData && tokenData.value) || "";
          console.log("[push] registration event fired. token=" + (t ? t.slice(0, 8) + "… (" + t.length + " chars)" : "(empty!)"));
          if (!t) return;
          try { localStorage.setItem("gt_pending_push_token", t); } catch (e) {}
          postPushTokenToServer(t, "registration-event");
        });

        PushNotifications.addListener("registrationError", function(err) {
          console.log("[push] registrationError: " + JSON.stringify(err || {}) +
            " — usually means Push Notifications capability is missing in Xcode or the provisioning profile.");
        });

        try {
          console.log("[push] calling PushNotifications.register()…");
          await PushNotifications.register();
          console.log("[push] register() returned (now waiting for 'registration' event)");
        } catch (err) {
          console.log("[push] register() threw: " + ((err && err.message) || err) +
            " — most likely Push Notifications capability is not enabled for this build.");
        }
      } catch (e) {
        console.log("[push] registerPush outer catch: " + ((e && e.message) || e));
      }
    }
    registerPush();

    // ── Flush the queued token once auth is available ──────────────────────
    // The 'registration' event usually fires BEFORE login, so the token gets
    // stashed in localStorage with no JWT to POST it. The old code only retried
    // on a 'gt-login' event, which on the phone didn't fire / raced the token
    // write — so the phone's token never reached the server. This polls until
    // the token is actually posted (every 1.5s for ~60s) and stops once a 200
    // removes the pending token. It also reacts immediately to 'gt-login'.
    var flushTimer = null;

    function flushPendingToken(source) {
      var pending = null;
      try { pending = localStorage.getItem("gt_pending_push_token"); } catch (e) {}
      if (!pending) return true;            // nothing queued → done
      if (!readAuthToken()) return false;   // not logged in yet → keep waiting
      console.log("[push] flushing pending token (source: " + source + ")");
      postPushTokenToServer(pending, source); // clears gt_pending_push_token on a 200
      return false;                          // keep polling until it's cleared
    }

    function startPolling() {
      if (flushTimer) return;
      var attempts = 0;
      flushTimer = setInterval(function() {
        attempts++;
        if (flushPendingToken("poll") || attempts > 40) {
          clearInterval(flushTimer);
          flushTimer = null;
        }
      }, 1500);
    }

    function retryPending() {
      if (!flushPendingToken("retry-on-auth")) startPolling();
    }

    window.addEventListener("gt-login", retryPending);
    retryPending();   // covers being already logged in at launch
    startPolling();   // covers login happening without a 'gt-login' event

    return function() {
      window.removeEventListener("gt-login", retryPending);
      if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
    };
  }, []);

  // NOTE: <IonApp> and <IonContent fullscreen> are deliberately NOT used here.
  // App.css overrides @ionic/react/css/core.css's scroll-locking (html height:100%,
  // body position:fixed, ion-app contain:size) so the document scrolls natively via
  // Capacitor's scrollEnabled:true on iOS WKWebView. Wrapping in IonContent caused
  // its inner scroller to collapse to 0px and hid all routed content. IonModal /
  // IonPage / IonIcon used inside child components still work without IonApp —
  // they are custom elements and IonModal portals to document.body when no
  // <ion-app> is found. setupIonicReact() is called once from src/index.js.
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App bg-black w-full">
          <Router>
            <DeepLinkHandler />
            <Header />
            <SuspensionBanner />
            <LoginModal />
            <OnboardingModal />
            <AliasModal />
            <DeleteModal />
            <RolePickerModal />
            {/* Single scroll container. The fixed header + bottom nav are
                siblings (rendered inside <Header/>) and stay pinned to the
                viewport because the document itself never scrolls. */}
            <div className="app-scroll">
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/*
                      "/" route:
                      - Native (Capacitor iOS): redirect straight to Discover.
                        The marketing hero is pointless for someone who already
                        installed the app; Discover is the most useful first screen.
                      - Web: show Main (marketing hero + download CTA).
                    */}
                    <Route path="/" element={isNative ? <Navigate to="/discover" replace /> : <Main />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/feed" element={<NewsPage />} />
                    <Route path="/feed/:slug" element={<ArticlePage />} />
                    <Route path="/rooms" element={<RoomsPage />} />
                    <Route path="/rooms/:sport" element={<RoomPage />} />
                    <Route path="/rooms/:sport/:entity" element={<RoomPage />} />
                    <Route path="/teams/:team" element={<TeamPage />} />
                    <Route path="/leagues/:league" element={<LeaguePage />} />
                    <Route path="/play" element={<Play />} />
                    <Route path="/standings" element={<Standings />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/discover" element={<Discover />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/children-privacy" element={<ChildrenPrivacy />} />
                    <Route path="/leagues" element={<LeaguesList />} />
                    <Route path="/league/:id" element={<LeagueDetail />} />
                    <Route path="/player/:alias" element={<PlayerProfile />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/parent" element={<ParentDashboard />} />
                    <Route path="/coach" element={<CoachDashboard />} />
                    <Route path="/coming-soon" element={<ComingSoon />} />
                    <Route path="/admin/moderation" element={<ModerationDashboard />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              <Footer />
            </div>
          </Router>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
